import { useEffect } from 'react';
import { LANGUAGES_CODES, routeUrl } from '../i18n/routes';
import { HTML_LANG } from '../utils/i18n';

const OG_LOCALE = {
  fr: 'fr_FR',
  en: 'en_US',
  de: 'de_DE',
  it: 'it_IT',
  es: 'es_ES',
  zh: 'zh_CN'
};

const setMeta = (name, content, isProperty = false) => {
  const attribute = isProperty ? 'property' : 'name';
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content || '');
};

const setLink = (rel, href, hreflang) => {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    if (hreflang) element.setAttribute('hreflang', hreflang);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const SEOHead = ({
  title,
  description,
  keywords,
  ogImage = 'https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png',
  url,
  routeKey,
  lang = 'fr',
  type = 'website',
  schemaData,
  schemas,
  noindex = false
}) => {
  const schemaList = schemas || (schemaData ? [schemaData] : []);
  const serializedSchemas = JSON.stringify(schemaList);
  const canonicalUrl = url || (routeKey ? routeUrl(routeKey, lang) : undefined);

  useEffect(() => {
    document.title = title;

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', type, true);
    setMeta('og:site_name', 'OptimHub', true);
    setMeta('og:locale', OG_LOCALE[lang] || 'en_US', true);
    if (canonicalUrl) setMeta('og:url', canonicalUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (canonicalUrl) setLink('canonical', canonicalUrl);

    // hreflang alternates
    if (routeKey) {
      LANGUAGES_CODES.forEach((code) => {
        setLink('alternate', routeUrl(routeKey, code), HTML_LANG[code] || code);
      });
      setLink('alternate', routeUrl(routeKey, 'en'), 'x-default');
    }

    // Structured data
    document.querySelectorAll('script[data-seo-schema="true"]').forEach((node) => node.remove());
    JSON.parse(serializedSchemas).forEach((schema) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [title, description, keywords, ogImage, canonicalUrl, routeKey, lang, type, noindex, serializedSchemas]);

  return null;
};

export default SEOHead;
